import { useEffect, type ReactNode } from 'react';
import { ApiError, NetworkError, photoUrl } from '../api';

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  const isNet = error instanceof NetworkError;
  const fields = error instanceof ApiError ? error.fields : [];
  return (
    <div className={`alert ${isNet ? 'alert-warn' : 'alert-error'}`} role="alert">
      <strong>{isNet ? 'Connection problem. ' : ''}</strong>
      {(error as Error).message}
      {fields.length > 0 && (
        <ul>
          {fields.map((f, i) => (
            <li key={i}>
              {f.path ? <code>{f.path}</code> : null} {f.message}
            </li>
          ))}
        </ul>
      )}
      {onRetry && (
        <div>
          <button className="btn btn-small" onClick={onRetry}>
            Try again
          </button>
        </div>
      )}
    </div>
  );
}

export const Success = ({ children }: { children: ReactNode }) => (
  <div className="alert alert-success" role="status">
    {children}
  </div>
);

export const Loading = ({ label = 'Loading…' }: { label?: string }) => (
  <div className="loading" role="status">
    <span className="spinner" /> {label}
  </div>
);

export const Empty = ({ children }: { children: ReactNode }) => <div className="empty">{children}</div>;

/** Shows a stored value, or a clear "missing" marker. Never substitutes a default. */
export const Val = ({ v, missing = 'not entered' }: { v: ReactNode | null | undefined; missing?: string }) =>
  v === null || v === undefined || v === '' ? <span className="missing">— {missing}</span> : <>{v}</>;

export function Money({ amount, currency }: { amount: string | null | undefined; currency: string | null | undefined }) {
  if (amount === null || amount === undefined) return <span className="missing">— no price</span>;
  return (
    <span className="money">
      {amount} {currency ?? <span className="missing">(no currency)</span>}
    </span>
  );
}

export const fmtDate = (s: string | null | undefined) => (s ? new Date(s).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '');

/** "Mixed lot" / "Incomplete 8 of 11" badge; nothing for a complete single-product lot. */
export function LotKindBadge({ lot }: { lot: { kind: string; completeness: string; piecesPresent: number | null; piecesTotal: number | null } }) {
  if (lot.kind === 'mixed') return <span className="badge badge-info">Mixed lot</span>;
  if (lot.completeness !== 'incomplete') return null;
  return <span className="badge badge-warn">Incomplete{lot.piecesPresent != null ? ` ${lot.piecesPresent} of ${lot.piecesTotal}` : ''}</span>;
}

export function Field({ label, hint, error, children }: { label: string; hint?: ReactNode; error?: string; children: ReactNode }) {
  return (
    <label className={`field ${error ? 'has-error' : ''}`}>
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export const Thumb = ({ id, size = 56 }: { id: string | null; size?: number }) =>
  id ? (
    <img className="thumb" src={photoUrl(id)} width={size} height={size} alt="" loading="lazy" />
  ) : (
    <span className="thumb thumb-none" style={{ width: size, height: size }}>
      no photo
    </span>
  );

export function Pager({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return <div className="pager muted">{total} result{total === 1 ? '' : 's'}</div>;
  return (
    <div className="pager">
      <button className="btn btn-small" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ‹ Prev
      </button>
      <span>
        Page {page} of {pages} · {total} results
      </span>
      <button className="btn btn-small" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next ›
      </button>
    </div>
  );
}

export const SOURCE_LABELS: Record<string, string> = {
  product_listing_title: "copied from product's listing title",
  product_manifest_title: 'copied from imported description',
  product_reference_price: "copied from product's reference price",
  product_manifest_price: 'copied from imported retail price',
  costco_page: 'from the Costco website',
  product_description: "copied from product's description",
  employee: 'entered by employee',
  none: 'not entered',
};

export const locationText = (l: { code: string; warehouse?: string | null; aisle?: string | null; rack?: string | null; shelf?: string | null; bin?: string | null } | null) => {
  if (!l) return null;
  const parts = [
    l.warehouse && `Whse ${l.warehouse}`,
    l.aisle && `Aisle ${l.aisle}`,
    l.rack && `Rack ${l.rack}`,
    l.shelf && `Shelf ${l.shelf}`,
    l.bin && `Bin ${l.bin}`,
  ].filter(Boolean);
  return parts.join(' · ');
};
